/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { InjectionToken } from '@angular/core';
import {
  OrganizationAllTimeMetrics,
  OrganizationMembersMetrics,
  OrganizationPageSummary,
} from '@app/app/page-modules/admin/components/analytics/admin-analytics.model';
import {
  AssessmentDatapointMetrics,
  AssessmentMemberMetricsSummary,
} from '@app/app/page-modules/resource/components/editor/analytics/assessment-analytics.model';
import { MemberReview } from '../../../../shared/models';
import { MembersBooleanSearchRequest, ReviewsSearchRequest } from '../../../../shared/models/admin/boolean-filters.model';
import {
  AccountInvitationsSummary,
  MemberActivity,
  MemberInvitationRequest,
  MemberInvitationResponse,
  MemberRecord,
  MemberRecordLearnerPlaylist,
  MemberRecordLearnerPlaylistCardsResponse,
  MemberRecordPlaylistCardResponse,
  MemberRecordPlaylistsRequest,
  MembersInvitationBulkResponse,
  MembersReIndexScope,
  MembersReIndexStatus,
  MembersSearchResponse,
  UserCardRecord,
  UserEnrollment,
  UserPlaylistRecord,
  UserSearch,
  ValidateEmailsFromCsvFileResponse,
} from '../../../../shared/models/admin/members.model';
import { StatsData, StatsSummary } from '../../../../shared/models/analytics/analytics.model';
import { Page } from '../../../../shared/models/page';
import { JoinedVia } from '../../../../shared/models/playlist/admin-enrolment.model';
import { ObservableResult } from '../../../../shared/store';

/** Injection token for the data service, used by angular to decide whether to inject Mock or API service at runtime. */
export const MEMBERS_DATA_SERVICE = new InjectionToken<MembersDataService>('[Admin Members] DataService');

/** The admin data service. */
export interface MembersDataService {
  addMembersToGroup(userGroupUid: string, request: string[]): ObservableResult<void>;

  removeMembersFromGroup(userGroupUid: string, request: string[]): ObservableResult<void>;

  loadMemberRecord(userUid?: string, playlistUid?: string): ObservableResult<MemberRecord>;

  getMemberEnrollments(userUid: string): ObservableResult<UserEnrollment[]>;

  getMemberActivities(memberId: string, page: number, size: number): ObservableResult<Page<MemberActivity>>;

  getUserPlaylistRecord(userUid: string, playlistUri: string): ObservableResult<UserPlaylistRecord>;

  getUserCardRecord(userUid: string, cardUri: string): ObservableResult<UserCardRecord>;

  getLearnerPlaylistCards(learnerPlaylistId: string): ObservableResult<MemberRecordLearnerPlaylistCardsResponse>;

  getPlaylistLearnerCardsByUserId(playlistUid: string, userUid: string): ObservableResult<MemberRecordPlaylistCardResponse[]>;

  getMemberPlaylistsByType(userUid: string, request: MemberRecordPlaylistsRequest): ObservableResult<Page<MemberRecordLearnerPlaylist>>;

  searchNonAdminUsers(page: number, pageSize: number, searchString: string): ObservableResult<Page<UserSearch>>;

  searchUsers(page: number, pageSize: number, searchString: string, organization?: string): ObservableResult<Page<UserSearch>>;

  getAllInvitationsList(
    page: number,
    pageSize: number,
    email?: string,
    groupUid?: string,
  ): ObservableResult<Page<MemberInvitationResponse>>;

  getAcceptedInvitationsList(page: number, pageSize: number, groupUid?: string): ObservableResult<Page<MemberInvitationResponse>>;

  getNotAcceptedInvitationsList(page: number, pageSize: number, groupUid?: string): ObservableResult<Page<MemberInvitationResponse>>;

  getInvitationsSummary(): ObservableResult<AccountInvitationsSummary>;

  inviteNewMembers(request: MemberInvitationRequest): ObservableResult<MembersInvitationBulkResponse>;

  importNewMembers(
    csvFile: File,
    groupIdsQueryParam: string,
    playlistIdsQueryParam: string,
  ): ObservableResult<MembersInvitationBulkResponse>;

  resendInvitation(invitationId: string): ObservableResult<void>;

  validateUsersFromCsvFile(csvFile: File, playlistUid: string): ObservableResult<ValidateEmailsFromCsvFileResponse>;

  getLearnerCard(playlistUid: string, userUid: string, cardUid: string): ObservableResult<MemberRecordPlaylistCardResponse>;

  getLearnerCardForReviewer(userUid: string, cardUid: string): ObservableResult<MemberRecordPlaylistCardResponse>;

  getStatisticsNewMembers(from: string, to: string): ObservableResult<StatsData[]>;

  getStatisticsActiveMembers(from: string, to: string): ObservableResult<StatsData[]>;

  getStatisticsSummary(): ObservableResult<StatsSummary>;

  searchMembers(request: MembersBooleanSearchRequest, page: number, size: number): ObservableResult<MembersSearchResponse>;

  searchCardMembers(
    request: MembersBooleanSearchRequest,
    playlistUid: string,
    cardUid: string,
    page: number,
    size: number,
  ): ObservableResult<MembersSearchResponse>;

  startMembersReIndex(scope: MembersReIndexScope): ObservableResult<MembersReIndexStatus>;

  getMembersReIndexStatus(scope: MembersReIndexScope): ObservableResult<MembersReIndexStatus>;

  searchStandaloneCardMembers(
    request: MembersBooleanSearchRequest,
    cardUid: string,
    page: number,
    size: number,
  ): ObservableResult<MembersSearchResponse>;

  searchPlaylistMembers(
    request: MembersBooleanSearchRequest,
    playlistUid: string,
    page: number,
    size: number,
  ): ObservableResult<MembersSearchResponse>;

  /**
   * Members enrolled on a playlist (course/programme), read from GET /courses/{playlistUid}/members. Returns a simple
   * name/role list with real backend pagination — distinct from searchPlaylistMembers, which uses the analytics
   * bool-search endpoint and carries richer (roles/status/progress) data.
   */
  getPlaylistMembers(playlistUid: string, page: number, size: number, term?: string): ObservableResult<PlaylistMembersPage>;

  booleanExportMembers(request: MembersBooleanSearchRequest): ObservableResult<string>;

  booleanExportPlaylistMembers(request: MembersBooleanSearchRequest, playlistUid: string): ObservableResult<string>;

  booleanExportCardMembers(request: MembersBooleanSearchRequest, playlistUid: string, cardUid: string): ObservableResult<string>;

  booleanExportEventCardMembers(request: MembersBooleanSearchRequest, playlistUid: string, eventUid: string): ObservableResult<string>;

  searchPlaylistReviews(
    request: ReviewsSearchRequest,
    playlistUid: string,
    page: number,
    size: number,
  ): ObservableResult<Page<MemberReview>>;

  searchCardReviews(request: ReviewsSearchRequest, cardUid: string, page: number, size: number): ObservableResult<Page<MemberReview>>;

  getActiveUsersNowCount(): ObservableResult<number>;

  getOrganizationAllTimeMetrics(): ObservableResult<OrganizationAllTimeMetrics>;

  getOrganizationPagesSummary(): ObservableResult<OrganizationPageSummary[]>;

  getOrganizationMembersMetrics(
    request: MembersBooleanSearchRequest,
    from: string,
    to: string,
  ): ObservableResult<OrganizationMembersMetrics>;

  getAssessmentMetrics(
    playlistUid: string,
    cardUid: string,
    from: string,
    to: string,
    request: MembersBooleanSearchRequest,
  ): ObservableResult<AssessmentDatapointMetrics>;

  getAssessmentMetricsSummary(
    playlistUid: string,
    cardUid: string,
    request: MembersBooleanSearchRequest,
  ): ObservableResult<AssessmentMemberMetricsSummary>;
}

/** A member of a playlist as returned by GET /courses/{id}/members (simple name/role/avatar shape). */
/** @property joinedVia - how an enrolled member of v3 content joined it, on lists that tell */
export interface PlaylistMember {
  id: string;
  name: string;
  position: string | null;
  avatarUrl: string | null;
  joinedVia?: JoinedVia | null;
  outsideAudience?: boolean;
}

/** A backend page of playlist members. */
export interface PlaylistMembersPage {
  members: PlaylistMember[];
  total: number;
}
